/**
 * Kandidatavimas (Applications History) Component
 * 
 * Displays employee's job application history in a grid layout.
 * Shows applied jobs with status, dates, and expandable history timeline.
 * 
 * @route /kandidatavimas
 * @access Employees only
 * 
 * Features:
 * - Grid layout of applied jobs (similar to job listings)
 * - Application status badges (pending, reviewed, accepted, rejected)
 * - Expandable "Istorija" (history) section with timeline
 * - Sort options (newest first, oldest first)
 * - Filter: "Nerodyti nebegaliojančių" (hide expired jobs)
 * - Delete application functionality (custom confirmation modal)
 * - Navigate to job details
 * 
 * Design:
 * - Uses global SCSS variables for consistency
 * - White background for company logos
 * - Brand colors for UI elements
 * - Responsive grid layout
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Briefcase, ChevronRight, ChevronDown, Eye, Mail, Trash2, AlertTriangle, Search, Plus } from 'lucide-react';
import useConfirmDialog from '../hooks/useConfirmDialog';
import './Kandidatavimas.scss';

interface JobApplication {
  id: number;
  job_id: number;
  job_title: string;
  company_name: string;
  company_logo?: string;
  location?: string;
  salary_min?: number;
  salary_max?: number;
  salary_currency?: string;
  job_deadline?: string;
  cv_title?: string;
  cover_letter_title?: string;
  status: 'pending' | 'reviewed' | 'accepted' | 'rejected';
  created_at: string;
  updated_at: string;
  viewed_at?: string;
}

const Kandidatavimas: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [selectedApplications, setSelectedApplications] = useState<number[]>([]);
  const [searchTitleFilter, setSearchTitleFilter] = useState('');

  useEffect(() => {
    if (isAuthenticated && (user?.user_type === 'employee' || user?.user_type === 'job_seeker')) {
      fetchApplications();
    }
  }, [isAuthenticated, user]);

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/job-applications/my`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Applications fetched:', result);
        setApplications(result.data || []);
      } else {
        console.error('Failed to fetch applications:', response.status);
        setErrorMessage('Nepavyko įkelti paraiškų');
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      setErrorMessage('Įvyko klaida kraunant paraiškas');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/job-applications/${deleteConfirmId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setDeleteConfirmId(null);
        fetchApplications();
      } else {
        setDeleteConfirmId(null);
        setErrorMessage('Nepavyko ištrinti paraiškos');
      }
    } catch (error) {
      console.error('Error deleting application:', error);
      setDeleteConfirmId(null);
      setErrorMessage('Įvyko klaida trinant paraišką');
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmId(null);
  };

  const toggleCard = (id: number) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedCards(newExpanded);
  };

  const filterApplicationsByTitle = (apps: JobApplication[]) => {
    if (!searchTitleFilter.trim()) return apps;
    
    const filterLower = searchTitleFilter.toLowerCase().trim();
    return apps.filter(app => 
      app.job_title.toLowerCase().includes(filterLower) ||
      app.company_name?.toLowerCase().includes(filterLower)
    );
  };

  const handleToggleSelectAll = () => {
    const filteredApps = filterApplicationsByTitle(applications);
    
    if (selectedApplications.length === filteredApps.length && filteredApps.length > 0) {
      setSelectedApplications([]);
    } else {
      setSelectedApplications(filteredApps.map(a => a.id));
    }
  };

  const handleToggleSelect = (id: number) => {
    setSelectedApplications(prev => 
      prev.includes(id) ? prev.filter(aid => aid !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedApplications.length === 0) return;

    if (!await confirm(`Ar tikrai norite ištrinti ${selectedApplications.length} paraiškas?`)) return;

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      await Promise.all(
        selectedApplications.map(id =>
          fetch(`${API_URL}/job-applications/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
        )
      );

      setSelectedApplications([]);
      fetchApplications();
    } catch (error) {
      console.error('Error deleting applications:', error);
      setErrorMessage('Įvyko klaida trinant paraiškas');
    }
  };

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="kandidatavimas-page">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  if (!isAuthenticated || (user?.user_type !== 'employee' && user?.user_type !== 'job_seeker')) {
    return <Navigate to="/" replace />;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatSalary = (min?: number, max?: number, currency: string = '€') => {
    if (!min && !max) return 'Atlyginimas nenurodytas';
    if (min && max) return `${min} - ${max} ${currency}`;
    if (min) return `nuo ${min} ${currency}`;
    if (max) return `iki ${max} ${currency}`;
    return '';
  };

  const filteredApplications = filterApplicationsByTitle(applications);

  return (
    <div className="kandidatavimas-page">
      {ConfirmDialogNode}
      <div className="container">
        {/* Header row: breadcrumbs left, filters right */}
        <div className="header-row">
          <div className="breadcrumbs">
            <span onClick={() => navigate('/')}>Pradžia</span>
            <span className="separator">&gt;</span>
            <span className="current">Kandidatavimo istorija</span>
          </div>
          <div className="filters-toolbar">
            <div className="select-all-dropdown">
              <button 
                className="select-all-btn"
                onClick={handleToggleSelectAll}
              >
                <input 
                  type="checkbox" 
                  checked={selectedApplications.length > 0 && selectedApplications.length === filteredApplications.length}
                  onChange={handleToggleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                />
                <span>Žymėti visus</span>
              </button>
            </div>
            <button 
              className="btn-delete"
              onClick={handleDeleteSelected}
              disabled={selectedApplications.length === 0}
            >
              <Trash2 size={16} />
              <span>Trinti</span>
            </button>
            <div className="search-input-wrapper">
              <input 
                type="text"
                className="search-title-input"
                placeholder="Skelbimo pavadinimas"
                value={searchTitleFilter}
                onChange={(e) => setSearchTitleFilter(e.target.value)}
              />
              <Search size={16} className="search-icon" />
            </div>
            <button className="btn-new-search" onClick={() => navigate('/darbo-skelbimai')}>
              <Plus size={16} />
              <span>Ieškoti darbų</span>
            </button>
          </div>
        </div>
        {errorMessage && (
          <div className="error-banner">
            {errorMessage}
            <button className="btn-close-error" onClick={() => setErrorMessage('')}>×</button>
          </div>
        )}

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Kraunama...</p>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="empty-state">
            <Briefcase size={64} />
            <h2>{searchTitleFilter ? 'Paraiškų pagal šį filtrą nerasta' : 'Dar neturite pateiktų paraiškų'}</h2>
            <p>{searchTitleFilter ? 'Pabandykite pakeisti paieškos kriterijus' : 'Raskite darbo skelbimą ir kandidatuokite'}</p>
            {!searchTitleFilter && (
              <button className="btn-primary" onClick={() => navigate('/darbo-skelbimai')}>
                Ieškoti darbų
              </button>
            )}
          </div>
        ) : (
          <div className="applications-grid">
            {filteredApplications.map((application) => (
              <div 
                key={application.id} 
                className={`application-card ${selectedApplications.includes(application.id) ? 'selected' : ''}`}
              >
                {/* Card Header with Checkbox and Actions */}
                <div className="card-header">
                  <div className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      checked={selectedApplications.includes(application.id)}
                      onChange={() => handleToggleSelect(application.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  
                  <button 
                    className="btn-delete-icon"
                    onClick={(e) => handleDeleteClick(application.id, e)}
                    title="Ištrinti paraišką"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {/* Job Info */}
                <div className="job-info">
                  <h3 
                    className="job-title"
                    onClick={() => navigate(`/job/${application.job_id}`)}
                  >
                    {application.job_title}
                  </h3>
                  
                  <p className="company-name">{application.company_name}</p>
                  
                  <div className="job-meta">
                    <div className="meta-item">
                      <span className="label">Atlyginimas</span>
                      <span className="value">{formatSalary(application.salary_min, application.salary_max, application.salary_currency)}</span>
                    </div>
                  </div>
                  
                  <div className="job-details">
                    {application.location && (
                      <div className="detail-item">
                        <MapPin size={14} />
                        <span>{application.location}</span>
                      </div>
                    )}
                    {application.job_deadline && (
                      <div className="detail-item">
                        <Calendar size={14} />
                        <span>galioja iki {formatDate(application.job_deadline)}</span>
                      </div>
                    )}
                    <div className="detail-item">
                      <Briefcase size={14} />
                      <span>Pastovūs</span>
                    </div>
                  </div>
                </div>

                {/* History Section */}
                <div className="history-section">
                  <button 
                    className="history-toggle"
                    onClick={() => toggleCard(application.id)}
                  >
                    {expandedCards.has(application.id) ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    Istorija
                  </button>
                  
                  {expandedCards.has(application.id) && (
                    <div className="history-timeline">
                      <div className="timeline-item">
                        <Eye size={16} />
                        <div className="timeline-content">
                          <span className="timeline-date">{formatDate(application.created_at)}</span>
                          <span className="timeline-text">Jūs kandidatavote į šią poziciją</span>
                        </div>
                      </div>
                      
                      {application.viewed_at && (
                        <div className="timeline-item">
                          <Eye size={16} />
                          <div className="timeline-content">
                            <span className="timeline-date">{formatDate(application.viewed_at)}</span>
                            <span className="timeline-text">Jūsų CV buvo peržiūrėtas</span>
                          </div>
                        </div>
                      )}
                      
                      {application.status === 'accepted' && (
                        <div className="timeline-item">
                          <Mail size={16} />
                          <div className="timeline-content">
                            <span className="timeline-date">{formatDate(application.updated_at)}</span>
                            <span className="timeline-text">Jums buvo išsiųstas laiškas į jonas@info.lt</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && ReactDOM.createPortal(
        <div className="confirm-modal-overlay" onClick={cancelDelete}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <AlertTriangle size={48} />
            </div>
            <h2>Ar tikrai norite ištrinti šią paraišką?</h2>
            <p>Šis veiksmas negrįžtamas ir paraiška bus ištrinta visam laikui.</p>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={cancelDelete}>
                Cancel
              </button>
              <button className="btn-confirm-delete" onClick={confirmDelete}>
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Kandidatavimas;
